import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TruncatedTextCell } from "@/components/global/Chat/components/dreamerReports/reportTableCells";
import { ReportActionsMenu } from "@/components/global/Chat/components/dreamerReports/ReportActionsMenu";
import { ReportNameInput } from "@/components/global/Chat/components/dreamerReports/ReportNameInput";
import { useReportRowActions } from "@/components/global/Chat/components/dreamerReports/useReportRowActions";
import { useReportRename } from "@/components/global/Chat/components/dreamerReports/useReportRename";
import { SavedReport } from "@/entities/savedReports/types";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

interface ReportNameCellProps {
  report: SavedReport;
  isRenaming: boolean;
  onFinishRename: () => void;
}

export const ReportNameCell: React.FC<ReportNameCellProps> = ({
  report,
  isRenaming,
  onFinishRename
}) => {
  const { t } = useTranslation();
  const { draftName, setDraftName, inputRef, commitRename, cancelRename } =
    useReportRename({ report, isRenaming, onFinish: onFinishRename });

  if (isRenaming) {
    return (
      <ReportNameInput
        ref={inputRef}
        value={draftName}
        onValueChange={setDraftName}
        onCommit={commitRename}
        onCancel={cancelRename}
      />
    );
  }

  // The table row opens the report; this button keeps it reachable from the
  // keyboard, and its click bubbles up to the row.
  return (
    <button
      type="button"
      className="block w-full truncate text-left"
      data-test="expanded-report-name"
    >
      <TruncatedTextCell as="span" variant="body">
        {report.name || t(`${TRANSLATION_KEY}.untitled`)}
      </TruncatedTextCell>
    </button>
  );
};

interface ReportActionsCellProps {
  report: SavedReport;
  onOpen: (report: SavedReport) => void;
  onStartRename: (report: SavedReport) => void;
  onSchedule: (report: SavedReport) => void;
  onRemoveSchedule: (report: SavedReport) => void;
  onDelete: (report: SavedReport) => void;
}

export const ReportActionsCell: React.FC<ReportActionsCellProps> = ({
  report,
  onOpen,
  onStartRename,
  onSchedule,
  onRemoveSchedule,
  onDelete
}) => {
  const handleOpen = useCallback(() => onOpen(report), [onOpen, report]);
  const handleStartRename = useCallback(
    () => onStartRename(report),
    [onStartRename, report]
  );
  const handleSchedule = useCallback(
    () => onSchedule(report),
    [onSchedule, report]
  );
  const handleRemoveSchedule = useCallback(
    () => onRemoveSchedule(report),
    [onRemoveSchedule, report]
  );

  const handleDelete = useCallback(() => onDelete(report), [onDelete, report]);

  const { actions } = useReportRowActions({
    report,
    onOpen: handleOpen,
    onStartRename: handleStartRename,
    onSchedule: handleSchedule,
    onRemoveSchedule: handleRemoveSchedule,
    onDelete: handleDelete
  });

  return (
    // Keeps the menu's clicks, which bubble out of its portal through React,
    // from also opening the row.
    <div
      className="flex items-center justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      <ReportActionsMenu actions={actions} />
    </div>
  );
};
